<template>
  <div class="block-item">
    <div
      class="_header"
      :style="{ display: 'flex', alignItems: 'center' }"
    >
      <div class="_handle">
        <c-button
          size="small"
          icon="drag"
          fab
          :style="{ cursor: 'move' }"
        />
      </div>
      <c-select
        :value="[block.type]"
        :options="options"
        show-border
        value-required
        @update:value="updateVal('type', $event[0])"
      />
      <div class="_right">
        <c-dropdown close-on-click>
          <c-button
            icon="more"
            fab
            size="small"
            color="light"
          />
          <template #content>
            <c-card contains-list>
              <c-card-item
                title="Delete"
                @click="$emit('delete', block)"
              />
            </c-card>
          </template>
        </c-dropdown>
      </div>
    </div>
    <div
      v-if="block.type === 'text'"
      class="_content-item -has-padding"
    >
      <tip-tap
        :value="textContent"
        :initial-content="block.textContent"
        :can-upload-images="hasStorage"
        @update:value="updateVal('textContent', $event)"
      />
    </div>
    <div v-if="block.type === 'ai'">
      <div class="_content-item -has-padding">
        <simple-text-editor
          :value="prompt"
          :initial-content="block.prompt"
          placeholder="Enter your prompt..."
          disable-enter
          return-string-value
          @update:value="updateVal('prompt', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CButton from 'shared/components/CButton.vue';
import CCard from 'shared/components/CCard.vue';
import CCardItem from 'shared/components/CCardItem.vue';
import CDropdown from 'shared/components/CDropdown.vue';
import CSelect from 'shared/components/CSelect.vue';
import SimpleTextEditor from 'shared/components/SimpleTextEditor.vue';
import TipTap from 'shared/components/TipTap.vue';
import {
  BETA_PHASE_MANAGER,
  C_BLOCK_TYPE_AI, C_BLOCK_TYPE_COOKIES, C_BLOCK_TYPE_LOCAL_STORAGE, C_BLOCK_TYPE_PAGE_URL,
  C_BLOCK_TYPE_SELECTION,
  C_BLOCK_TYPE_TEXT,
  C_BLOCK_TYPE_VISIBLE_AREA_SCREENSHOT, USER_SERVICE,
} from 'shared/constants';
import useSubscription from 'shared/services/subscription';
import { inject } from 'vue';

export default {
  name: 'BlockItem',
  props: {
    block: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const userService = inject(USER_SERVICE);
    const betaPhaseManager = inject(BETA_PHASE_MANAGER);
    const { hasStorage } = useSubscription(userService.currentUser, betaPhaseManager);
    return { hasStorage };
  },
  components: { CSelect, TipTap, SimpleTextEditor, CDropdown, CCard, CCardItem, CButton },
  data() {
    return {
      textContent: null,
      prompt: null,
      options: [
        {
          text: 'Text',
          value: C_BLOCK_TYPE_TEXT,
          icon: this.getTypeIcon(C_BLOCK_TYPE_TEXT),
          color: 'plain',
        },
        {
          text: 'Page URL',
          value: C_BLOCK_TYPE_PAGE_URL,
          icon: this.getTypeIcon(C_BLOCK_TYPE_PAGE_URL),
          color: 'plain',
        },
        {
          text: 'Screenshot',
          value: C_BLOCK_TYPE_VISIBLE_AREA_SCREENSHOT,
          icon: this.getTypeIcon(C_BLOCK_TYPE_VISIBLE_AREA_SCREENSHOT),
          color: 'plain',
        },
        {
          text: 'Selected text',
          value: C_BLOCK_TYPE_SELECTION,
          icon: this.getTypeIcon(C_BLOCK_TYPE_SELECTION),
          color: 'plain',
        },
        {
          text: 'AI',
          value: C_BLOCK_TYPE_AI,
          icon: this.getTypeIcon(C_BLOCK_TYPE_AI),
          color: 'plain',
        },
        {
          text: 'Cookies',
          value: C_BLOCK_TYPE_COOKIES,
          icon: this.getTypeIcon(C_BLOCK_TYPE_COOKIES),
          color: 'plain',
        },
        {
          text: 'Local storage',
          value: C_BLOCK_TYPE_LOCAL_STORAGE,
          icon: this.getTypeIcon(C_BLOCK_TYPE_LOCAL_STORAGE),
          color: 'plain',
        },
      ],
    };
  },
  emits: ['update:value', 'delete'],
  methods: {
    updateVal(key, val) {
      if (key === 'type' && val === 'ai') {
        this.$emit('update:value', { ...this.block, type: val, prompt: 'Summarize the text.' });
        return;
      }
      this.$emit('update:value', { ...this.block, [key]: val });
    },
    getTypeIcon(type) {
      switch (type) {
        case C_BLOCK_TYPE_TEXT:
          return 'font-size';
        case C_BLOCK_TYPE_PAGE_URL:
          return 'link';
        case C_BLOCK_TYPE_VISIBLE_AREA_SCREENSHOT:
          return 'screenshot';
        case C_BLOCK_TYPE_SELECTION:
          return 'highlight';
        case C_BLOCK_TYPE_AI:
          return 'pro';
        case C_BLOCK_TYPE_COOKIES:
          return 'code';
        case C_BLOCK_TYPE_LOCAL_STORAGE:
          return 'code';
        default:
          return '';
      }
    },
  },
};
</script>

<style scoped lang="scss" type="text/scss">
.block-item {
  padding: 0.4rem 0.6rem 0.4rem;
  border: 1px solid map-get($grey, "lighten-4");
  background-color: map-get($grey, "lighten-7");
  border-radius: 0.4rem;

  ._header {
    ._right {
      margin-left: auto;
    }

    ._handle {
      position: relative;
      width: 2.4rem;
      height: 2.4rem;
      margin-right: 0.4rem;
    }

    ._handle-btn {
      position: absolute;
      cursor: move;

      &:hover {
        background-color: $hover-color;
      }
    }

    ._type-icon {
      position: absolute;
      display: flex;
      opacity: 1;
      visibility: visible;
      transition: opacity 0.4s ease-in-out, background 20ms ease-in 0s;
    }
  }

  ._content-item {
    margin: 0.8rem 0 0.2rem;
    background-color: white;
    border-radius: 0.4rem;

    &.-has-padding {
      border: 1px solid map-get($grey, "lighten-3");
      padding: 0.6rem;
    }
  }
}
</style>
